import { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as ChevronLeft } from "@icons/arrow-left.svg";
import { ReactComponent as ChevronRight } from "@icons/arrow-right.svg";

export default function ScrollRow({ children, cardStyle, arrowOffset = -32 }) {
  const rowRef = useRef(null);
  const dragRef = useRef({ isDown: false, moved: false, startX: 0, scrollLeft: 0 });
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const [hoveredArrow, setHoveredArrow] = useState(null);
  const [isDraggingRow, setIsDraggingRow] = useState(false);

  const checkArrows = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 2);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    checkArrows();
    const el = rowRef.current;
    el?.addEventListener("scroll", checkArrows, { passive: true });
    return () => el?.removeEventListener("scroll", checkArrows);
  }, [checkArrows]);

  const scroll = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
    setTimeout(checkArrows, 450);
  };

  const startRowDrag = (event) => {
    const el = rowRef.current;
    if (!el || event.button !== 0) return;
    dragRef.current = {
      isDown: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
    };
  };

  const moveRowDrag = (event) => {
    const el = rowRef.current;
    if (!el || !dragRef.current.isDown) return;
    const deltaX = event.clientX - dragRef.current.startX;
    if (!dragRef.current.moved && Math.abs(deltaX) < 7) return;
    if (!dragRef.current.moved) {
      dragRef.current.moved = true;
      setIsDraggingRow(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    el.scrollLeft = dragRef.current.scrollLeft - deltaX;
    checkArrows();
  };

  const endRowDrag = (event) => {
    if (!dragRef.current.isDown) return;
    const moved = dragRef.current.moved;
    dragRef.current.isDown = false;
    dragRef.current.moved = false;
    setIsDraggingRow(false);
    if (moved) {
      event.preventDefault();
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    setTimeout(checkArrows, 80);
  };

  const arrowZoneStyle = (side, visible) => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    [side]: arrowOffset,
    width: 82,
    zIndex: 6,
    display: visible ? "flex" : "none",
    alignItems: "center",
    justifyContent: side === "left" ? "flex-start" : "flex-end",
    padding: side === "left" ? "0 0 0 32px" : "0 32px 0 0",
    pointerEvents: visible ? "auto" : "none",
  });

  const arrowEdgeFadeStyle = (side, active) => ({
    position: "absolute",
    inset: 0,
    zIndex: 0,
    pointerEvents: "none",
    opacity: active ? 1 : 0,
    background:
      side === "left"
        ? "linear-gradient(90deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.18) 42%, rgba(0,0,0,0) 100%)"
        : "linear-gradient(270deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.18) 42%, rgba(0,0,0,0) 100%)",
    transition: "opacity 0.45s cubic-bezier(.22, 1, .36, 1)",
  });

  const arrowStyle = (visible, side) => ({
    background: "rgba(110,110,110, 0.12)",
    color: "rgba(255,255,255, 0.5)",
    width: 65,
    height: 89,
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    position: "relative",
    zIndex: 1,
    opacity: visible && hoveredArrow === side ? 1 : 0,
    transition: "opacity 0.35s ease",
  });

  return (
    <div className="scroll-row-shell" style={{ position: "relative" }}>
      <div className="scroll-row-track"
        onMouseEnter={() => setHoveredArrow("left")}
        onMouseLeave={() => setHoveredArrow(null)}
        style={arrowZoneStyle("left", showLeft)}
      >
        <div style={arrowEdgeFadeStyle("left", hoveredArrow === "left")} />
        <button
          onClick={() => scroll(-1)}
          style={{ ...arrowStyle(showLeft, "left"), border: "none" }}
        >
          <ChevronLeft width={24} height={24} />
        </button>
      </div>
      <div
        ref={rowRef}
        onPointerDown={startRowDrag}
        onPointerMove={moveRowDrag}
        onPointerUp={endRowDrag}
        onPointerCancel={endRowDrag}
        onPointerLeave={endRowDrag}
        style={{
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: cardStyle,
          gap: 10,
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          paddingBottom: 4,
          cursor: isDraggingRow ? "grabbing" : "grab",
          touchAction: "pan-y",
          userSelect: "none",
        }}
      >
        {children}
      </div>
      <div
        onMouseEnter={() => setHoveredArrow("right")}
        onMouseLeave={() => setHoveredArrow(null)}
        style={arrowZoneStyle("right", showRight)}
      >
        <div style={arrowEdgeFadeStyle("right", hoveredArrow === "right")} />
        <button
          onClick={() => scroll(1)}
          style={{ ...arrowStyle(showRight, "right"), border: "none" }}
        >
          <ChevronRight width={24} height={24} />
        </button>
      </div>
    </div>
  );
}
